import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PinDialog } from "@/components/PinDialog";
import { formatMoney, parseAmountToCents, cleanCashtag } from "@/lib/money";
import { getDeviceId } from "@/lib/device";
import { toast } from "sonner";
import { Loader2, AlertCircle, ShieldAlert, Store, CheckCircle2 } from "lucide-react";

interface MerchantProfile {
  user_id: string;
  display_name: string;
  cashtag: string;
  avatar_url: string | null;
  is_merchant: boolean;
  store_name: string | null;
}

const Pay = () => {
  const { cashtag: rawTag } = useParams<{ cashtag: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const tag = cleanCashtag(rawTag ?? "");
  const lockedAmount = params.get("amount");
  const presetNote = params.get("note") ?? "";

  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [amount, setAmount] = useState(lockedAmount ?? "");
  const [note, setNote] = useState(presetNote);
  const [balance, setBalance] = useState<number | null>(null);
  const [hasPinSet, setHasPinSet] = useState<boolean | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);
  const [idemKey, setIdemKey] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!tag) { setNotFound(true); setLoading(false); return; }
      const { data } = await supabase
        .from("public_profiles")
        .select("user_id, display_name, cashtag, avatar_url, is_merchant, store_name")
        .eq("cashtag", tag)
        .maybeSingle();
      if (!data) setNotFound(true);
      else setMerchant(data as MerchantProfile);
      setLoading(false);
    })();
  }, [tag]);

  useEffect(() => {
    if (!user) return;
    supabase.from("wallets").select("balance_cents").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setBalance((data as any)?.balance_cents ?? 0));
    supabase.rpc("has_pin").then(({ data }) => setHasPinSet(Boolean(data)));
  }, [user]);

  const cents = parseAmountToCents(amount) ?? 0;
  const insufficient = user && balance !== null && cents > balance;
  const isOwner = user?.id === merchant?.user_id;

  const handlePay = () => {
    if (!merchant || !cents) return;
    if (!user) {
      const url = `/pay/${tag}${lockedAmount ? `?amount=${lockedAmount}` : ""}`;
      navigate(`/auth?next=${encodeURIComponent(url)}`);
      return;
    }
    if (!hasPinSet) { toast.error("Set a wallet PIN in Settings first"); return; }
    setIdemKey(crypto.randomUUID());
    setPinOpen(true);
  };

  const handleConfirm = async (pin: string) => {
    if (!merchant || !cents) return;
    const key = idemKey ?? crypto.randomUUID();
    if (!idemKey) setIdemKey(key);
    setPaying(true);
    const { data, error } = await supabase.functions.invoke("send-money", {
      body: {
        recipient_cashtag: merchant.cashtag,
        amount_cents: cents,
        pin,
        note: note || null,
        device_id: getDeviceId(),
        idempotency_key: key,
      },
    });
    setPaying(false);
    const errMsg = (error as any)?.message || (data as any)?.error;
    if (errMsg) { toast.error(errMsg); return; }
    setPinOpen(false);
    setIdemKey(null);
    setDone(true);
    toast.success(`Paid ${formatMoney(cents)} 🌴`);
  };

  if (loading || authLoading) {
    return (
      <MobileShell>
        <BrandHeader />
        <div className="flex-1 grid place-items-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </MobileShell>
    );
  }

  if (notFound || !merchant) {
    return (
      <MobileShell>
        <BrandHeader onBack={() => navigate("/")} />
        <div className="px-5 py-12 flex-1 flex flex-col items-center text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground" />
          <h2 className="mt-3 font-display text-xl font-bold">Merchant not found</h2>
          <p className="mt-1 text-sm text-muted-foreground">This payment link is invalid.</p>
          <Button asChild variant="hero" size="xl" className="mt-6">
            <Link to="/">Go home</Link>
          </Button>
        </div>
      </MobileShell>
    );
  }

  if (done) {
    return (
      <MobileShell>
        <BrandHeader />
        <div className="px-5 py-12 flex-1 flex flex-col items-center text-center animate-fade-in-up">
          <CheckCircle2 className="h-16 w-16 text-success" />
          <h2 className="mt-4 font-display text-2xl font-bold">Payment sent</h2>
          <p className="mt-2 text-3xl font-display font-extrabold text-primary">{formatMoney(cents)}</p>
          <p className="text-sm text-muted-foreground mt-1">
            to {merchant.store_name || merchant.display_name}
          </p>
          <div className="mt-8 w-full space-y-2">
            <Button asChild variant="hero" size="xl" className="w-full">
              <Link to="/wallet">Back to wallet</Link>
            </Button>
          </div>
        </div>
      </MobileShell>
    );
  }

  const displayName = merchant.store_name || merchant.display_name;

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Pay merchant</h1>
      </BrandHeader>

      <div className="px-5 py-7 flex-1 flex flex-col animate-fade-in-up">
        <div className="flex flex-col items-center">
          <div className="h-16 w-16 rounded-full bg-primary/10 grid place-items-center text-primary font-display text-2xl font-bold overflow-hidden">
            {merchant.avatar_url ? (
              <img src={merchant.avatar_url} alt={displayName} className="h-full w-full object-cover" />
            ) : merchant.is_merchant ? (
              <Store className="h-7 w-7" />
            ) : (
              displayName?.[0]?.toUpperCase() ?? "?"
            )}
          </div>
          <p className="mt-3 font-display text-lg font-bold">{displayName}</p>
          <p className="text-xs text-muted-foreground">${merchant.cashtag}</p>
          {merchant.is_merchant && (
            <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-[11px] font-medium">
              <Store className="h-3 w-3" /> Verified merchant
            </span>
          )}
        </div>

        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-sm font-medium">Amount</Label>
            <div className="mt-2 flex items-center bg-secondary/60 rounded-xl px-5 h-16">
              <span className="font-display text-3xl font-bold text-muted-foreground mr-1">$</span>
              <input
                inputMode="decimal"
                autoFocus={!lockedAmount}
                disabled={!!lockedAmount}
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-transparent flex-1 font-display text-3xl font-bold outline-none w-full disabled:opacity-100"
              />
            </div>
            {user && (
              <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                <span>Balance: {balance !== null ? formatMoney(balance) : "—"}</span>
                {lockedAmount && <span>Amount set by merchant</span>}
              </div>
            )}
            {insufficient && <p className="mt-1 text-xs text-destructive">Not enough balance</p>}
          </div>

          <div>
            <Label className="text-sm font-medium">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={140}
              placeholder="What's it for?"
              className="mt-2 h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
            />
          </div>

          {user && hasPinSet === false && (
            <Link
              to="/settings"
              className="flex items-center gap-2 rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs"
            >
              <ShieldAlert className="h-4 w-4 text-destructive shrink-0" />
              <span className="flex-1">Set a wallet PIN before paying.</span>
              <span className="text-destructive font-medium">Set up</span>
            </Link>
          )}

          {isOwner ? (
            <p className="text-center text-sm text-muted-foreground">
              This is your own merchant link.
            </p>
          ) : (
            <Button
              onClick={handlePay}
              variant="hero"
              size="xl"
              className="w-full"
              disabled={paying || cents === 0 || !!insufficient}
            >
              {paying ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : user ? (
                `Pay ${cents ? formatMoney(cents) : ""}`.trim()
              ) : (
                "Sign in to pay"
              )}
            </Button>
          )}
        </div>
      </div>

      <PinDialog
        open={pinOpen}
        title={`Pay ${formatMoney(cents)}`}
        description={`To ${displayName}`}
        onCancel={() => { setPinOpen(false); setIdemKey(null); }}
        onSubmit={handleConfirm}
        pending={paying}
      />

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default Pay;
